import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reorder, useDragControls } from 'motion/react';

import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import Iconify from '../../../theme/iconify/iconify';
import { useResponsive } from '../../../hooks/use-responsive';

import { mislecAPI } from '../../../util/axios';

function Section({ section, index, reorder, sectionVisibility, setOpen, setMessage, setSeverity, updateUser }) {
    const [grab, setGrab] = React.useState(false);
    const [hover, setHover] = React.useState(false);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const anchorRect = menuAnchor ? menuAnchor.getBoundingClientRect() : null;

    const FORMAT = [
        {
            format: 'JSON',
            icon: 'si:json-alt-1-duotone',
        },
        {
            format: 'CSV',
            icon: 'fe:table',
        }
    ];

    const { t } = useTranslation();
    const controls = useDragControls();
    const mdUp = useResponsive('up', 'md');

    const exportResults = (section, format) => {
        mislecAPI.get(`api/course/exams/${section.exam}`)
            .then((res) => {
                //  Export exam results to JSON file
                if (format === 'JSON') {
                    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(res.data.exams, null, 2));
                    const downloadAnchorNode = document.createElement('a');
                    downloadAnchorNode.setAttribute('href', dataStr);
                    downloadAnchorNode.setAttribute('download', `mISLec_exam_${section.exam}.json`);
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                }

                //  Export exam results to CSV file
                if (format === 'CSV') {
                    //ignore "quiz" and "original_quiz" fields
                    const csvRows = [];
                    const headers = Object.keys(res.data.exams[0]).filter(key => key !== 'quiz' && key !== 'original_quiz');
                    headers.push(...Object.keys(res.data.exams[0].quiz).filter(key => key !== 'questions' && key !== 'is_solving'));
                    csvRows.push(headers.join(','));

                    for (const exam of res.data.exams) {
                        const values = [];
                        for (const header of headers) {
                            if (header in exam) {
                                values.push(`"${exam[header]}"`);
                            } else if (header in exam.quiz) {
                                values.push(`"${exam.quiz[header]}"`);
                            } else {
                                values.push('""');
                            }
                        }
                        csvRows.push(values.join(','));
                    }
                    const csvString = csvRows.join('\n');
                    const dataStr = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString);
                    const downloadAnchorNode = document.createElement('a');
                    downloadAnchorNode.setAttribute('href', dataStr);
                    downloadAnchorNode.setAttribute('download', `mISLec_exam_${section.exam}.csv`);
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                }
            })
            .catch((error) => {
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const handleOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleClose = () => {
        setMenuAnchor(null);
    };

    return (
        <Reorder.Item
            value={section}
            dragListener={false}
            dragControls={controls}
            style={{
                listStyle: 'none',
                paddingTop: '8px',
                paddingBottom: '8px'
            }}>
            <Grid container alignItems='center'>
                <Grid item xs={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                        title={t('course.modals.newQuiz.questions.tooltips.reorder')}
                        open={!grab && hover}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        arrow
                    >
                        <Iconify
                            key={section.id}
                            icon='lsicon:drag-filled'
                            width={26}
                            onPointerDown={(e) => {
                                controls.start(e);
                                setGrab(true);
                            }}
                            onPointerUp={(e) => {
                                reorder();
                                setGrab(false);
                                setHover(false);
                            }}
                            sx={{ cursor: grab ? 'grabbing' : 'grab' }}
                        />
                    </Tooltip>
                </Grid>
                <Grid item xs={11.5} sx={{ border: '1px solid rgba(33, 43, 54, 0.08)', borderRadius: '8px', p: 1, backgroundColor: '#FFF', zIndex: grab ? 1 : 0 }}>
                    <Grid container sx={{ width: '100%' }}>
                        <Grid item xs={10}>
                            <Tooltip title={t(`course.tabs.settings.sections.tooltips.sectionType.${section.type}`)} arrow>
                                <IconButton
                                    sx={{
                                        borderRadius: '8px',
                                        p: 0.5,
                                        px: 1,
                                        mr: 1,
                                        backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                        },
                                    }}
                                    disableFocusRipple
                                    disableRipple
                                >
                                    <Iconify icon={section.type === 'content' ? 'majesticons:text' : 'fluent:shifts-availability-24-regular'} width={18} sx={{ color: 'primary.main' }} />
                                </IconButton>
                            </Tooltip>
                            <Typography sx={{ fontSize: '0.8rem', display: 'inline' }}>
                                {section.title}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {section?.type === 'exam' &&
                                <>
                                    <Tooltip title={t('course.tabs.settings.sections.tooltips.exportExam')} arrow>
                                        <IconButton
                                            onClick={handleOpen}
                                            sx={{
                                                p: 0.5,
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='material-symbols:download' />
                                        </IconButton>
                                    </Tooltip>
                                    <Popover
                                        open={!!menuAnchor}
                                        anchorReference={mdUp ? 'anchorEl' : 'anchorPosition'}
                                        anchorPosition={!mdUp && anchorRect ? { top: anchorRect.bottom, left: window.innerWidth / 2 } : undefined}
                                        anchorEl={menuAnchor}
                                        onClose={handleClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: mdUp ? 'right' : 'center' }}
                                        transformOrigin={{ vertical: 'top', horizontal: mdUp ? 'right' : 'center' }}
                                        PaperProps={{
                                            sx: {
                                                p: 0,
                                                mt: 1,
                                                ml: mdUp ? 0.75 : 0
                                            },
                                        }}
                                    >
                                        {FORMAT.map((format) => (
                                            <MenuItem
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setMenuAnchor(null);
                                                    exportResults(section, format.format);
                                                }}
                                                sx={{
                                                    my: 0.5
                                                }}
                                            >
                                                <Grid container alignItems='center'>
                                                    <Grid item xs={1} sx={{ display: 'flex' }}>
                                                        <ListItemIcon sx={{ minWidth: '100% !important', minHeight: '0px !important', mr: 1.5 }}>
                                                            <Iconify icon={format.icon} sx={{ color: 'primary.main' }} />
                                                        </ListItemIcon>
                                                    </Grid>
                                                    <Grid item xs={11}>
                                                        <Grid container sx={{ pl: 1 }}>
                                                            <Grid item xs={12}>
                                                                <ListItemText sx={{ fontSize: '0.85rem', whiteSpace: 'wrap' }}>
                                                                    {t(`course.tabs.settings.sections.buttons.export${format.format}`)}
                                                                </ListItemText>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </MenuItem>
                                        ))}
                                    </Popover>
                                    <Divider orientation='vertical' flexItem sx={{ mx: '4px', my: '6px' }} />
                                </>
                            }
                            <Tooltip title={section?.visible ? t('course.tooltips.hideSection') : t('course.tooltips.showSection')} arrow>
                                <IconButton
                                    onClick={() => sectionVisibility(index)}
                                    sx={{
                                        p: 0.5,
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Iconify icon={section?.visible ? 'eva:eye-fill' : 'eva:eye-off-fill'} width={20} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Reorder.Item>
    );
}

export default Section;